import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, Lock, Phone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { showSuccessToast, showErrorToast } from "@/utils/toast";
import { useAuth } from "@/contexts/AuthContext";
import { isPhoneNumber, normalizePhone } from "@/utils/phone";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

type Step = 'email' | 'verify' | 'password';

const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>('email');
  const { setInPasswordRecovery } = useAuth();

  // Set password recovery flag when entering verify step
  useEffect(() => {
    if (step === 'verify' || step === 'password') {
      setInPasswordRecovery(true);
    }
    return () => {
      // Only clear if we're unmounting completely (not just step change)
    };
  }, [step, setInPasswordRecovery]);

  // Step 1: Send email (or look up email from phone, then send)
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let resetEmail = email;

      // If user entered a phone number, look up their email first
      if (isPhoneNumber(email)) {
        const normalizedPhone = normalizePhone(email);
        const { data, error: lookupError } = await supabase.functions.invoke('check-email-exists', {
          body: { phone: normalizedPhone }
        });

        if (lookupError || !data?.exists || !data?.userId) {
          showErrorToast("No account found with this phone number");
          setIsLoading(false);
          return;
        }

        // Look up the email from the user's admin record
        const { data: adminData } = await supabase
          .from('admins')
          .select('email')
          .eq('id', data.userId)
          .single();

        if (!adminData?.email) {
          showErrorToast("No email associated with this phone number. Please contact support.");
          setIsLoading(false);
          return;
        }

        resetEmail = adminData.email;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail);
      if (error) throw error;

      // Update email state so the verify step shows the correct email
      setEmail(resetEmail);

      const isPhoneInput = isPhoneNumber(email);
      showSuccessToast(
        isPhoneInput
          ? "We sent a reset code to the email associated with your account"
          : "Password reset code has been sent to your email"
      );
      setStep('verify');
    } catch (error: unknown) {
      console.error("Password reset email error:", error);
      showErrorToast(error instanceof Error ? error.message : "Error sending reset code");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Just validate code format and move to password step
  // We don't call verifyOtp here to avoid authenticating the user prematurely
  const handleVerifyCode = () => {
    if (otp.length !== 6) {
      showErrorToast("Please enter the complete 6-digit code");
      return;
    }

    // Just move to password step - actual verification happens when setting password
    setStep('password');
  };

  // Step 3: Verify OTP and set new password together
  // This is where authentication actually happens
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      showErrorToast("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);
    setInPasswordRecovery(true); // Prevent redirects during this process

    try {
      // First verify the OTP - this authenticates the user
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery',
      });

      if (verifyError) {
        // If code is invalid, go back to verify step
        setStep('verify');
        throw verifyError;
      }

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      showSuccessToast("Password updated successfully!");
      setInPasswordRecovery(false);
      setTimeout(() => onBack(), 1500);
    } catch (error: unknown) {
      console.error("Password reset error:", error);
      showErrorToast(error instanceof Error ? error.message : "Invalid code or error updating password. Please try again.");
      setInPasswordRecovery(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      showSuccessToast("A new reset code has been sent to your email");
    } catch (error: unknown) {
      showErrorToast(error instanceof Error ? error.message : "Error resending code");
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Set new password
  if (step === 'password') {
    return (
      <form onSubmit={handleSetPassword} className="space-y-4">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-serif font-semibold text-brand-primary">Create New Password</h2>
          <p className="text-sm text-brand-secondary mt-2">
            Enter your new password below
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-brand-secondary" />
            <Input
              id="newPassword"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="pl-9"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-brand-secondary" />
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-9"
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary/90"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating Password...
            </>
          ) : (
            'Update Password'
          )}
        </Button>
      </form>
    );
  }

  // STEP 2: Verify code
  if (step === 'verify') {
    return (
      <div className="space-y-4">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-serif font-semibold text-brand-primary">Enter Reset Code</h2>
          <p className="text-sm text-brand-secondary mt-2">
            We sent a 6-digit code to <strong>{email}</strong>
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={setOtp}
              disabled={isLoading}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        <Button
          onClick={handleVerifyCode}
          disabled={otp.length !== 6 || isLoading}
          className="w-full bg-brand-primary hover:bg-brand-primary/90"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify Code'
          )}
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">Didn't receive the code?</p>
          <Button
            variant="link"
            onClick={handleResendCode}
            disabled={isLoading}
            className="text-sm"
          >
            {isLoading ? 'Sending...' : 'Resend code'}
          </Button>
        </div>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={onBack}
          disabled={isLoading}
        >
          Back to Login
        </Button>
      </div>
    );
  }

  // STEP 1: Enter email or phone
  const isPhone = isPhoneNumber(email);
  const IdentifierIcon = isPhone ? Phone : Mail;

  return (
    <form onSubmit={handleSendEmail} className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-serif font-semibold text-brand-primary">Reset Password</h2>
        <p className="text-sm text-brand-secondary mt-2">
          Enter your email or phone number and we'll send you a code to reset your password.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email or Phone Number</Label>
        <div className="relative">
          <IdentifierIcon className="absolute left-3 top-3 h-4 w-4 text-brand-secondary" />
          <Input
            id="email"
            type="text"
            placeholder="Email or phone number"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-9"
            required
            disabled={isLoading}
          />
        </div>
      </div>

      <Button
        type="submit"
        className="w-full bg-brand-primary hover:bg-brand-primary/90"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          'Send Reset Code'
        )}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={isLoading}
      >
        Back to Login
      </Button>
    </form>
  );
};

export default ForgotPasswordForm;
